<template>
    <kiosk-layout>
        <div class="flex flex-col gap-12 lg:flex-row">
            <div class="shrink-0 lg:w-60">
                <kiosk-groups-sidebar v-model="fetchParams"></kiosk-groups-sidebar>
            </div>
            <div class="w-full">
                <kiosk-groups-controls v-model="fetchParams" @reset="resetFilters"></kiosk-groups-controls>
                <page-loading v-if="loading && !list.length"></page-loading>

                <empty-state
                    v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                    @reset="resetFilters"
                />

                <div v-else-if="list.length">
                    <div class="text-xl font-extrabold">Results</div>
                    <kiosk-groups-table>
                        <kiosk-group-row
                            v-for="(row, index) in response?.data?.data || []"
                            v-model="(response?.data?.data || [])[index]"
                            :key="row.group_id"
                        ></kiosk-group-row>
                    </kiosk-groups-table>
                </div>

                <div v-else-if="isEqual(defaultFilters(), fetchParams)">
                    <div v-if="favorites.length" class="text-xl font-extrabold">Favorites</div>
                    <kiosk-groups-table v-if="favorites.length">
                        <kiosk-group-row
                            v-for="(row, index) in favorites"
                            v-model="favorites[index]"
                            :key="row.group_id"
                        ></kiosk-group-row>
                    </kiosk-groups-table>

                    <div class="text-xl font-extrabold">Recents</div>

                    <kiosk-groups-table>
                        <kiosk-group-row
                            v-for="(row, index) in recents"
                            v-model="recents[index]"
                            :key="row.group_id"
                        ></kiosk-group-row>
                    </kiosk-groups-table>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import EmptyState from '~/components/general/EmptyState.vue';
import { computed, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskGroupRow from '~/components/kiosk/kiosk-groups/list/KioskGroupRow.vue';
import KioskGroupsControls from '~/components/kiosk/kiosk-groups/list/KioskGroupsControls.vue';
import KioskGroupsSidebar from '~/components/kiosk/kiosk-groups/details/KioskGroupsSidebar.vue';
import KioskGroupsTable from '~/components/kiosk/kiosk-groups/list/KioskGroupsTable.vue';
import { useToasted } from '~/composables/useToasted';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { adminGroupsClient } from '~/services';
import { isEqual } from '~/utils/helpers';

const toasted = useToasted();

const defaultFilters = () => ({
    team_id: undefined,
    query: '',
    status: ['draft', 'active', 'suspended', 'closed'],
    sort_dir: 'desc',
    sort_field: 'created_at',
    per_page: 100,
});

const fetchParams = ref(defaultFilters());

const { loading, response, fetchData } = useVersionedRequest();

const list = computed(() => response.value?.data?.data || []);

const listCancelKey = Symbol('kiosk-groups');

watch(
    fetchParams,
    () => {
        filterData();
    },
    {
        deep: true,
    }
);

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    filterData();
};

const filterData = () => {
    fetchData(adminGroupsClient.getGroups(fetchParams.value, { cancelKey: listCancelKey }));
};

const favorites = ref([]);
const recents = ref([]);

// on first load favorite groups and recent groups are loaded
const loadInitialData = async () => {
    loading.value = true;

    try {
        const favoritesResponse = await adminGroupsClient.getGroups({ is_favorite: 1, per_page: 100 });
        const recentsResponse = await adminGroupsClient.getGroups({
            is_favorite: 0,
            sort_dir: 'desc',
            sort_field: 'created_at',
            per_page: 100,
        });

        favorites.value = favoritesResponse.data.data;
        recents.value = recentsResponse.data.data;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

loadInitialData();
</script>
